import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Battery, Zap, Euro, Cpu } from 'lucide-react';
import { useConfigurator } from '../ConfiguratorProvider';
import { inverterOptions } from '../productData';

export const InverterSelection = () => {
  const { configuration, setConfiguration } = useConfigurator();

  const handleInverterSelect = (inverter: typeof inverterOptions[0]) => {
    setConfiguration({ inverter });
  };

  const getRecommendedInverter = () => {
    if (!configuration.panel) return null;
    const totalWattage = configuration.panel.wattage * configuration.panelCount;
    const recommendedPower = totalWattage * 0.8; // 80% of panel power
    
    return inverterOptions.find(inv => 
      inv.power >= recommendedPower && inv.power <= totalWattage
    ) || inverterOptions[0];
  };

  const recommendedInverter = getRecommendedInverter();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold mb-2">Kies je omvormer</h3>
        <p className="text-muted-foreground">
          De omvormer zet gelijkstroom van de panelen om naar wisselstroom voor je huis
        </p>
      </div>

      {configuration.panel && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Cpu className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Aanbeveling:</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Voor {configuration.panelCount}x {configuration.panel.wattage}W panelen 
              ({(configuration.panel.wattage * configuration.panelCount / 1000).toFixed(1)} kWp) 
              raden we een omvormer van {Math.round((configuration.panel.wattage * configuration.panelCount * 0.8) / 100) * 100}W 
              tot {configuration.panel.wattage * configuration.panelCount}W aan.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {inverterOptions.map((inverter) => {
          const isRecommended = recommendedInverter?.id === inverter.id;
          
          return (
            <Card 
              key={inverter.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                configuration.inverter?.id === inverter.id 
                  ? 'ring-2 ring-primary bg-primary/5' 
                  : ''
              } ${isRecommended ? 'border-blue-300 bg-blue-50' : ''}`}
              onClick={() => handleInverterSelect(inverter)}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {inverter.brand}
                      {isRecommended && (
                        <Badge variant="outline" className="text-blue-600 border-blue-300">
                          Aanbevolen
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>{inverter.model}</CardDescription>
                  </div>
                  {inverter.hybrid && (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                      <Battery className="h-3 w-3 mr-1" />
                      Hybrid
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-orange-500" />
                      <span className="font-medium">{inverter.power}W</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Euro className="h-4 w-4 text-green-500" />
                      <span className="font-medium">€{inverter.price}</span>
                    </div>
                  </div>
                  
                  <div className="text-sm text-muted-foreground">
                    {inverter.hybrid ? (
                      <span className="text-green-700">
                        ✓ Geschikt voor toekomstige batterij-uitbreiding
                      </span>
                    ) : (
                      <span>
                        Standaard omvormer voor directe aansluiting
                      </span>
                    )}
                  </div>

                  <div className="bg-muted/50 p-3 rounded-lg">
                    <div className="text-sm text-muted-foreground mb-1">
                      Inclusief 0% BTW:
                    </div>
                    <div className="font-bold text-lg">
                      €{inverter.price.toLocaleString()}
                    </div>
                  </div>

                  {configuration.inverter?.id === inverter.id && (
                    <Button className="w-full" variant="secondary">
                      Geselecteerd ✓
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {configuration.inverter && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Cpu className="h-4 w-4 text-primary" />
              <span className="font-medium">Jouw selectie:</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {configuration.inverter.brand} {configuration.inverter.model} - 
              {configuration.inverter.power}W voor €{configuration.inverter.price}
              {configuration.inverter.hybrid && " (Hybrid - batterij-ready)"}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};